import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { addDays, subMonths } from 'date-fns';

import { NSCommonConstants, NSCommons } from '@ruby/configs/common.constants';
import { NSDiversionsConstants } from '@ruby/configs/diversions.constants';
import {
  IDiversionsCustomerDetails,
  IDiversionsPreviousRequestFilters,
  IDiversionsPrevReqPayload,
  IDiversionsStatusRequest
} from '@ruby/shared/models/diversions/diversions.interface';

@Component({
  selector: 'ng-previous-requests-form',
  templateUrl: './previous-requests.form.html',
  styleUrls: ['./previous-requests.form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PreviousRequestsForm implements OnDestroy, OnChanges {
  @Input() loading!: boolean;
  @Input() statusAndRequest!: IDiversionsStatusRequest;
  @Input() selectedCustomerDetails?: Partial<IDiversionsCustomerDetails>;
  @Input() isInternal?: boolean;
  @Input() filters?: IDiversionsPreviousRequestFilters;
  @Input() keepFilters?: boolean;
  @Output() submitPrevReq = new EventEmitter<IDiversionsPrevReqPayload>();
  @Output() saveFilters = new EventEmitter<IDiversionsPreviousRequestFilters>();
  @Output() resetState = new EventEmitter();
  @ViewChild('selectStatus') selectStatus!: MatOption;
  request: IDiversionsPrevReqPayload = {};
  today = new Date();
  previousRequestForm: UntypedFormGroup = new UntypedFormGroup({
    equipmentId: new UntypedFormControl('', [Validators.pattern(NSCommonConstants.validEquipment)]),
    requestNumber: new UntypedFormControl('', [Validators.pattern(NSCommonConstants.validRequestNumber)]),
    waybillSerial: new UntypedFormControl('', [Validators.pattern(NSCommonConstants.validWaybill)]),
    status: new UntypedFormControl(''),
    type: new UntypedFormControl(''),
    city: new UntypedFormControl('', [Validators.pattern(NSCommonConstants.alphanumericName)]),
    dateFrom: new UntypedFormControl(addDays(subMonths(new Date(), 1), 1), Validators.required),
    dateTo: new UntypedFormControl(this.today, Validators.required)
  });

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedCustomerDetails?.currentValue) {
      if (changes.filters?.currentValue && changes.keepFilters?.currentValue) {
        this.previousRequestForm.patchValue({
          ...changes.filters?.currentValue,
          dateTo: new Date(changes.filters?.currentValue.dateTo),
          dateFrom: new Date(changes.filters?.currentValue.dateFrom)
        });
        this.previousRequestForm.markAsDirty();
      }

      this.setDefaultFormRequest(changes.selectedCustomerDetails.currentValue.primary10 || changes.selectedCustomerDetails.currentValue.primary6,
        changes.keepFilters?.currentValue && changes.filters?.currentValue || this.previousRequestForm.value);
      this.submitForm();
    }
  }

  ngOnDestroy(): void {
    if (this.previousRequestForm.dirty) {
      this.saveFilters.emit({
        ...this.previousRequestForm.value,
        dateTo: this.previousRequestForm.value.dateTo.getTime(),
        dateFrom: this.previousRequestForm.value.dateFrom.getTime()
      });
    }
  }

  /**
   * Submit
   *
   * @summary Submits previous request form
   * @returns void
   */
  submitForm(): void {
    this.setDefaultFormRequest(this.request.customerNumber, this.previousRequestForm.value);
    this.submitPrevReq.emit({ ...this.request });
  }

  /**
   * Reset
   *
   * @summary Reset's the form and set's default values
   * @returns void
   */
  reset(): void {
    this.previousRequestForm.reset({
      equipmentId: '',
      requestNumber: '',
      waybillSerial: '',
      dateFrom: addDays(subMonths(new Date(), 1), 1),
      dateTo: this.today
    });
    this.setDefaultFormRequest(this.request.customerNumber);
  }

  private setDateField(val?: Date): void {
    if (val) {
      this.request.selectionDateData = `${ NSCommons.formatNSDate(new Date(this.previousRequestForm.controls.dateFrom.value),
        'MM/dd/yyyy') },${ NSCommons.formatNSDate(new Date(this.previousRequestForm.controls.dateTo.value), 'MM/dd/yyyy') }`;
      this.request.selectionDateField = 'DATE';
    } else {
      this.request.selectionDateData = NSDiversionsConstants.getPreviousRequestDateFormat();
      this.request.selectionDateField = 'DATE';
    }
  }

  private setDefaultFormRequest(customerNumber?: string, filters?: IDiversionsPreviousRequestFilters): void {
    if (filters && this.isInternal) {
      this.request = {
        ...NSDiversionsConstants.prevReqDefault
      };

      if (filters.city) {
        this.request.selectionCityData = filters.city.toLocaleUpperCase();
        this.request.selectionCityField = 'CITY';
      }

      if (filters.type) {
        this.request.selectionRtypData = filters.type;
        this.request.selectionRtypField = 'RTYP';
      }

      if (filters.status) {
        this.request.selectionStatData = filters.status;
        this.request.selectionStatField = 'STAT';
      }

      if (filters.waybillSerial) {
        this.request.selectionSerData = filters.waybillSerial;
        this.request.selectionSerField = 'SER';
      }

      if (filters.requestNumber) {
        this.request.selectionConfData = filters.requestNumber;
        this.request.selectionConfField = 'CONF';
      }

      if (filters.equipmentId) {
        this.request.selectionEqidData = filters.equipmentId;
        this.request.selectionEqidField = 'EQID';
      }

      if (filters.dateFrom) {
        this.setDateField(new Date(filters.dateFrom));
      }

      if (filters.dateTo) {
        this.setDateField(new Date(filters.dateTo));
      }
    } else {
      if (this.isInternal) {
        this.request = {
          ...NSDiversionsConstants.prevReqDefault
        };
        this.setDateField();
      }
    }

    if (customerNumber) {
      this.request.customerNumber = customerNumber;
    }
  }
}
